/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import QRCode from 'qrcode'
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as DataForm from '@/Components/Global/DataForm/template.vue'
import * as SubmitButton from '@/Components/Global/SubmitButton/template.vue'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @internal Const @name StorageFormQR                                                          **/
/**************************************************************************************************/
const StorageFormQR = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'StorageFormQR', mixins: [MixinBase, MixinComponent, MixinResponsive],

	components:
	{
		DataForm: DataForm.default,
		SubmitButton: SubmitButton.default
	},

	props:
	{
		show: Boolean
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			states:
			{
				dataQR: 'DATA_EXAMPLE_QR',
				downloadName: 'IMAGE_DOWNLOAD_QR',
				hasAccess: false,
				imageQR: undefined as string | undefined,
				isFetching: false,
				labelQR: 'QR SIN HABILITAR',
				optionsQR: { errorCorrectionLevel: 'H', margin: 0, scale: 24, type: 'image/png' } as QRCode.QRCodeToDataURLOptions
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	created() : void
	{
		this.ResetQR()
	},

	mounted() : void
	{
		this.GenerateQR()
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		DataForm() : DataForm.DataFormRef
		{
			return this.$refs.dataForm as DataForm.DataFormRef
		},

		GetSubmitButtonText() : string
		{
			return this.states.hasAccess ? 'Deshabilitar QR Planta' : 'Habilitar QR Planta'
		},

		GetSubmitButtonVariant() : string
		{
			return this.states.hasAccess ? 'red' : 'green'
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		async DownloadQR() : Promise<void>
		{
			try
			{
				const { imageQR } = this.states

				// Crear un elemento <a>.
				const anchor    = document.createElement('a')
				anchor.href     = imageQR || '#'
				anchor.download = `${ this.states.downloadName }.png`

				// Agregarlo al documento para descargar el QR como imagen.
				document.body.appendChild(anchor)
				anchor.click()
				document.body.removeChild(anchor)
			}
			catch (error)
			{
				PrintHelper.error('[StorageFormQR]:downloadQR() Exception Raised')
				PrintHelper.error('[StorageFormQR]:downloadQR() Error: ', error)
			}
		},

		async GenerateQR() : Promise<void>
		{
			try
			{
				const data = await QRCode.toDataURL(this.states.dataQR, this.states.optionsQR)
				this.Set<StorageFormQRRef['states']>({ imageQR: data })
			}
			catch (error)
			{
				PrintHelper.error('[StorageFormQR]:generateQR() Exception Raised')
				PrintHelper.error('[StorageFormQR]:generateQR() Error: ', error)
			}
		},

		OnSFQRClick(key: string) : void
		{
			PrintHelper.warning(`[StorageFormQR]:onSFQRClick(key=${ key }) Method Trigered`)
			this.$emit('onSFQRClick', key)
		},

		OnSFQRSubmitClick() : void
		{
			PrintHelper.warning('[StorageFormQR]:onSFQRSubmitClick() Method Trigered')
			this.$emit('onSFQRSubmitClick', this.states.hasAccess)
		},

		ResetQR() : void
		{
			this.Set<StorageFormQRRef['states']>(
			{
				dataQR: 'DATA_EXAMPLE_QR',
				downloadName: 'IMAGE_DOWNLOAD_QR',
				hasAccess: false,
				imageQR: undefined,
				labelQR: 'QR SIN HABILITAR'
			})
		}
	}
})

/**************************************************************************************************/
/** @export Component @name StorageFormQR                                                        **/
/**************************************************************************************************/
export default StorageFormQR

/**************************************************************************************************/
/** @export Type @name StorageFormQRRef                                                          **/
/**************************************************************************************************/
export type StorageFormQRRef = InstanceType<typeof StorageFormQR>